import { RangedValue } from './../utils/RangedValue';
import { MathUtil } from './../utils/MathUtil';
import { FW } from './../Framework';
import { ArrayUtil } from './../utils/ArrayUtil';
import { Button } from './../display/GafManager';

export class ScrollControl extends PIXI.Container {

    static EVENT = {
        SCROLL: "scroll",
        SCROLLING_STOPPED: "scrollingStopped",
        SCROLL_START: "scrollStart",
        SCROLL_END: "scrollEnd"
    };

    private _touchRect: PIXI.Rectangle;

    private _scrollNode: PIXI.Container;

    private _scrollButton: PIXI.DisplayObject;

    private _scrollButtonRange: RangedValue;

    private _scrollRange: RangedValue;

    private _currentScroll: number;

    private _scrollStartValue: number;

    private _touchTarget: PIXI.DisplayObject;

    private _scrollTouch: PIXI.Point;
    private _scrollSpeed: number = 0;
    private _lastScrollTime: number = 0;
    private _scrollMouseStart: PIXI.Point;

    private _scrollButtonInverse: boolean = false;

    private _isScrollingPreventOtherTouch: boolean = false;

    private _buttonsOfScrollingCheck: Array<Button> = [];

    private _isScrolling: boolean = false;

    private _onScrollBar: boolean = false;

    constructor(_touchRect: PIXI.Rectangle) {
        super();
        this.interactive = true;
        this._touchRect = _touchRect;
        this.createMask();

        this._scrollNode = new PIXI.Container();
        this.addChild(this._scrollNode);
        this.hitArea = _touchRect;
    }

    private createMask(): void {
        var mask: PIXI.Graphics = new PIXI.Graphics();
        mask.beginFill(0xFF0000, 0.5);
        mask.drawRect(this._touchRect.x, this._touchRect.y, this._touchRect.width, this._touchRect.height);
        mask.endFill();
        this.addChild(mask);

        this.mask = mask;
    }

    public get scrollButtonInverse(): boolean {
        return this._scrollButtonInverse;
    }

    public set scrollButtonInverse(value: boolean) {
        this._scrollButtonInverse = value;
    }

    public get currentScroll(): number {
        return this._currentScroll;
    }

    public dispose(): void {
        this.offTouchTarget();
        this._touchTarget = null;
        FW.removeUpdateFunction(this, this.scrollUpdate);
        this.removeChildren();
        this._scrollNode.removeChildren();
        if (this.parent) {
            this.parent.removeChild(this);
        }
    }

    public get scrollNode(): PIXI.Container {
        return this._scrollNode;
    }

    public get scrollButton(): PIXI.DisplayObject {
        return this._scrollButton;
    }

    public initScrollButton(button: PIXI.DisplayObject, moveRangeMin: number, moveRangeMax: number): void {
        this._scrollButton = button;
        this._scrollButtonRange = new RangedValue(moveRangeMin, moveRangeMax);
    }

    public setScrollRange(min: number, max: number, _currentScroll: number): void {
        this._scrollRange = new RangedValue(min, max);
        this.setCurrentScroll(this._scrollRange.restrictValue(_currentScroll), true);
    }

    public get scrollRange(): RangedValue {
        return this._scrollRange;
    }

    private offTouchTarget(): void {
        if (this._touchTarget) {
            this._touchTarget
                .on('mousedown', this.onDragStart, this)
                .on('touchstart', this.onDragStart, this)
                .on('mouseup', this.onDragEnd, this)
                .on('mouseupoutside', this.onDragEnd, this)
                .on('touchend', this.onDragEnd, this)
                .on('touchendoutside', this.onDragEnd, this)
                .on('mousemove', this.onDragMove, this)
                .on('touchmove', this.onDragMove, this);
        }
        FW.removeUpdateFunction(this, this.scrollUpdate);
    }

    public start(touchTarget: PIXI.DisplayObject = null): void {
        this.offTouchTarget();
        this._touchTarget = touchTarget ? touchTarget : this;
        this._touchTarget
            .on('mousedown', this.onDragStart, this)
            .on('touchstart', this.onDragStart, this)
            .on('mouseup', this.onDragEnd, this)
            .on('mouseupoutside', this.onDragEnd, this)
            .on('touchend', this.onDragEnd, this)
            .on('touchendoutside', this.onDragEnd, this)
            .on('mousemove', this.onDragMove, this)
            .on('touchmove', this.onDragMove, this);

            FW.addUpdateFunction(this, this.scrollUpdate);

        this.emit(ScrollControl.EVENT.SCROLL, this.scrollNodeValue);
    }

    public hitTest(localPoint: PIXI.Point): PIXI.DisplayObject {
        var child: PIXI.DisplayObject = null;

        if (this.visible && this._touchRect.contains(localPoint.x, localPoint.y)) {
            var globalPoint: PIXI.Point = this.toGlobal(localPoint);
            child = FW.hitTest(globalPoint, this);
            if (child == null) {
                return this;
            }
        }
        return child;
    }

    public isScrollingPreventOtherTouch(): boolean {
        return this._isScrollingPreventOtherTouch;
    }

    public isScrolling(): boolean {
        return this._isScrolling;
    }

    protected getPosValue(pos: PIXI.Point): number {
        return pos.y;
    }

    private onDragStart(event: any): void {
        var pos: PIXI.Point = event.data.getLocalPosition(this);
        this.startScroll(pos, this.isOnScrollBar(this.toGlobal(pos)));
    }
    private onDragMove(event: any): void {
        if (this._scrollMouseStart == null)
            return;

        var pos: PIXI.Point = event.data.getLocalPosition(this);
        if (Math.abs(this.getPosValue(this._scrollMouseStart) - this.getPosValue(this._scrollTouch)) > 3 && !this._isScrollingPreventOtherTouch) {
            this.scrollNode.interactive = false;
            this._isScrollingPreventOtherTouch = true;
            this.makeUpStateOfButtonsOfScrolling();
        }

        if (this._onScrollBar) {
            var diff: number = this.getPosValue(pos) - this.getPosValue(this._scrollTouch);
            diff *= this._scrollRange.range / this._scrollButtonRange.range;
            this.setCurrentScroll(this._scrollRange.restrictValue(this._scrollStartValue - diff), true);
        }
        else {
            var speed: number = this.getPosValue(pos) - this.getPosValue(this._scrollTouch);
            this.setCurrentScroll(this.scrollNodeValue + speed, true);
            this._scrollTouch = pos;

            speed /= Math.max(1, - this._lastScrollTime);
            speed = speed > 0 ? Math.sqrt(speed) : -Math.sqrt(-speed);
            speed = Math.max(-20, Math.min(20, speed));
            this._scrollSpeed = MathUtil.numberFollowTarget(this._scrollSpeed, speed, 0.3, 1);
        }
        this._lastScrollTime = FW.time;
        //trace("pScrollSpeedY = " + pScrollSpeedY);
    }
    private onDragEnd(_event: any): void {
        this.stopScroll();
    }

    protected isOnScrollBar(globalPoint: PIXI.Point): boolean {
        if (this._scrollButton && this._scrollButton.parent) {
            this._scrollButton.hitArea
            return FW.hitTest(globalPoint, this._scrollButton) != null;
        }
        return false;
    }

    public setCurrentScroll(value: number, udpateNow: boolean): void {
        this._currentScroll = this._scrollRange.restrictValue(value);
        if (udpateNow) {
            this.setScrollDisplay(value, 1);
        }
    }

    public get scrollNodeValue(): number {
        return this._scrollNode.y;
    }
    public set scrollNodeValue(value: number) {
        this._scrollNode.y = value;
    }

    public get scrollButtonValue(): number {
        return this._scrollButton ? this._scrollButton.y : Number.NaN;
    }
    public set scrollButtonValue(value: number) {
        if (this._scrollButton)
            this._scrollButton.y = value;
    }

    protected setScrollDisplay(scroll: number, ratio: number = 0.3): void {
        var wasScrolling: Boolean = this._isScrolling;
        this._isScrolling = scroll != this.scrollNodeValue;

        if (this._isScrolling)
            this.scrollNodeValue = MathUtil.numberFollowTarget(this.scrollNodeValue, scroll, ratio, 1);

        if (this._scrollButtonRange) {
            if (this.scrollButtonInverse) {
                if (this._scrollRange.min == this._scrollRange.max)
                    this.scrollButtonValue = this._scrollButtonRange.max;
                else
                    this.scrollButtonValue = this._scrollButtonRange.calcValueInRange(this._scrollRange.calcPercentOfValue(this.scrollNodeValue));
            }
            else {
                if (this._scrollRange.min == this._scrollRange.max)
                    this.scrollButtonValue = this._scrollButtonRange.min;
                else
                    this.scrollButtonValue = this._scrollButtonRange.calcValueInRange(1 - this._scrollRange.calcPercentOfValue(this.scrollNodeValue));
            }
        }

        if (this._isScrolling)
            this.emit(ScrollControl.EVENT.SCROLL, this.scrollNodeValue);
        else if (wasScrolling)
            this.emit(ScrollControl.EVENT.SCROLLING_STOPPED);
    }

    private startScroll(pos: PIXI.Point, onScrollBar: boolean): void {
        this._onScrollBar = onScrollBar;
        this._isScrollingPreventOtherTouch = false;
        this._scrollMouseStart = pos.clone();
        this._scrollTouch = pos;
        this._scrollSpeed = 0;
        this._currentScroll = this.scrollNodeValue;
        this._scrollStartValue = this._currentScroll;
        this._lastScrollTime = FW.time;
        this.emit(ScrollControl.EVENT.SCROLL_START);
    }
    private stopScroll(): void {
        this.scrollNode.interactive = true;
        this._isScrollingPreventOtherTouch = false;
        this._scrollTouch = null;
        this._scrollMouseStart = null;
        this.emit(ScrollControl.EVENT.SCROLL_END);
    }
    private scrollUpdate(dt: number): void {
        if (this.visible && this.parent && !this._onScrollBar) {
            if (this._scrollSpeed != 0) {
                var decrase: number = 0.01;
                if (this._scrollSpeed > 0) {
                    this._scrollSpeed -= decrase * dt;
                    if (this._scrollSpeed <= 0) {
                        this._scrollSpeed = 0;
                    }
                }
                else {
                    this._scrollSpeed += decrase * dt;
                    if (this._scrollSpeed >= 0) {
                        this._scrollSpeed = 0;
                    }
                }

                if (this._scrollTouch == null) {
                    this.setCurrentScroll(this._currentScroll + this._scrollSpeed * dt, false);
                }
            }

            if (this._scrollTouch == null)
                this.setScrollDisplay(this._currentScroll);
        }
    }

    public addButtonOfScrollingCheck(button: Button): void {
        if (ArrayUtil.addUniqueElement(this._buttonsOfScrollingCheck, button)) {
            button.on(Button.EVENT.STATE_CHANGE, function (state: string): void {
                if (state == Button.STATE.DOWN) {
                    if (ArrayUtil.removeElement(this._buttonsOfScrollingCheck, button)) {
                        // make it at the head of the array
                        this._buttonsOfScrollingCheck.unshift(button);
                    }
                }
            });
        }
    }

    private makeUpStateOfButtonsOfScrolling(): void {
        for (var button of this._buttonsOfScrollingCheck) {
            if (button.state == Button.STATE.DOWN) {
                button.state = Button.STATE.OUT;
            }
            else {
                break;
            }
        }
    }
}


// WEBPACK FOOTER //
// ./src/framework/interactive/ScrollControl.ts